import { useQuery } from '@tanstack/react-query'
import type { ConvocationStatus, OfficialDocumentKind } from '@concours/shared'
import { useAuth } from '@concours/ui'

/** Convocations et documents officiels (phase 17). */
export const DOCUMENTS_KEY = ['admin', 'official-documents'] as const

export interface Room {
  id: string
  name: string
  capacity: number
  position: number
  used: number
}

export interface Center {
  id: string
  name: string
  city: string | null
  address: string | null
  capacity: number
  rooms: Room[]
}

export interface OfficialDocument {
  id: string
  kind: OfficialDocumentKind
  kindLabel: string
  code: string
  title: string
  template: string
  bytes: number
  sha256: string
  generatedAt: string | null
  revokedAt: string | null
  revokedReason: string | null
}

export interface Batch {
  id: string
  status: 'queued' | 'running' | 'done' | 'failed'
  total: number
  done: number
  error: string | null
  ready: boolean
  createdAt: string | null
}

export interface ConvocationRow {
  id: string
  applicationNumber: string
  name: string
  center: string
  room: string
  seat: number
  scheduledAt: string | null
  status: ConvocationStatus
  statusLabel: string
  document: boolean
  issuedAt: string | null
  downloadedAt: string | null
}

export interface ConvocationsOverview {
  stage: {
    id: string
    label: string
    position: number
    status: string
    startsAt: string | null
    competitionId: string
  }
  population: number
  summary: { draft: number; issued: number; withDocument: number }
  convocations: ConvocationRow[]
  documents: OfficialDocument[]
  batches: Batch[]
}

export function useCenters(competitionId: string) {
  const { session } = useAuth()
  return useQuery({
    queryKey: [...DOCUMENTS_KEY, 'centers', competitionId],
    queryFn: () =>
      session.api
        .get<{ data: Center[] }>(`/admin/competitions/${competitionId}/centers`)
        .then((r) => r.data),
    enabled: Boolean(competitionId),
  })
}

export function useConvocations(stageId: string) {
  const { session } = useAuth()
  return useQuery({
    queryKey: [...DOCUMENTS_KEY, 'convocations', stageId],
    queryFn: () => session.api.get<ConvocationsOverview>(`/admin/stages/${stageId}/convocations`),
    // Suivi d'une génération groupée en cours.
    refetchInterval: (q) =>
      q.state.data?.batches.some((b) => b.status === 'queued' || b.status === 'running')
        ? 2000
        : false,
  })
}

export function useStageDocuments(stageId: string, enabled = true) {
  const { session } = useAuth()
  return useQuery({
    queryKey: [...DOCUMENTS_KEY, 'stage-documents', stageId],
    queryFn: () =>
      session.api
        .get<{ data: OfficialDocument[] }>(`/admin/stages/${stageId}/documents`)
        .then((r) => r.data),
    enabled,
  })
}

/** Enregistre un fichier téléchargé (PDF, ZIP). */
export function saveFile(file: { blob: Blob; filename?: string | null }, fallback: string) {
  const url = URL.createObjectURL(file.blob)
  const a = document.createElement('a')
  a.href = url
  a.download = file.filename ?? fallback
  a.click()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

/** Ouvre un PDF dans un nouvel onglet (aperçu). */
export function openFile(file: { blob: Blob }) {
  const url = URL.createObjectURL(file.blob)
  window.open(url, '_blank', 'noopener')
  setTimeout(() => URL.revokeObjectURL(url), 60_000)
}

export interface HubStage {
  id: string
  label: string
  position: number
  status: string
  startsAt: string | null
  draft: number
  issued: number
  documents: number
}

export interface HubCompetition {
  id: string
  reference: string
  title: string
  session: string | null
  centers: number
  capacity: number
  stages: HubStage[]
}

export function useDocumentsHub() {
  const { session } = useAuth()
  return useQuery({
    queryKey: [...DOCUMENTS_KEY, 'hub'],
    queryFn: () =>
      session.api.get<{ data: HubCompetition[] }>('/admin/documents/hub').then((r) => r.data),
  })
}

/** Date-heure de Dakar (GMT, sans heure d'été) ↔ champ `datetime-local`. */
export const toLocalInput = (iso: string | null | undefined) => (iso ? iso.slice(0, 16) : '')
export const fromLocalInput = (value: string) => `${value}:00Z`

export const CONVOCATION_VARIANT = {
  draft: 'neutral',
  issued: 'success',
  cancelled: 'danger',
} as const

export const BATCH_LABELS: Record<Batch['status'], string> = {
  queued: 'En attente',
  running: 'En cours',
  done: 'Terminée',
  failed: 'Échec',
}
